import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Subscription, catchError, interval, of } from 'rxjs'

const BELL_DOCKED_TO_USER_MENU_STORAGE_KEY = 'fs.notifications-bell.docked-to-user-menu'

export type UserNotification = {
  id: string
  title: string
  message: string
  type?: string
  link?: string
  metadata?: Record<string, unknown>
  readAt?: string | null
  createdAt?: string | null
}

type NotificationsResponse = {
  unreadCount: number
  items: UserNotification[]
}

type NotificationsPanelAnchor = 'bell' | 'menu'

export type NotificationsState = NotificationsResponse & {
  loading: boolean
  panelOpen: boolean
  bellDockedToUserMenu: boolean
  panelAnchor: NotificationsPanelAnchor
  panelTop: number | null
  panelBottom: number | null
  panelLeft: number | null
  panelRight: number | null
  panelMaxHeight: number | null
}

const INITIAL_STATE: NotificationsState = {
  unreadCount: 0,
  items: [],
  loading: false,
  panelOpen: false,
  bellDockedToUserMenu: false,
  panelAnchor: 'bell',
  panelTop: null,
  panelBottom: null,
  panelLeft: null,
  panelRight: null,
  panelMaxHeight: null,
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly stateSubject = new BehaviorSubject<NotificationsState>(INITIAL_STATE)
  readonly state$ = this.stateSubject.asObservable()

  private pollingSubscription?: Subscription
  private loading = false

  constructor(private readonly http: HttpClient) {}

  initializeBellDocking() {
    this.patchState({
      bellDockedToUserMenu: localStorage.getItem(BELL_DOCKED_TO_USER_MENU_STORAGE_KEY) === 'true',
    })
  }

  startPolling() {
    if (this.pollingSubscription) {
      return
    }

    void this.load()
    this.pollingSubscription = interval(60000).subscribe(() => {
      void this.load(false)
    })
  }

  stopPolling() {
    this.pollingSubscription?.unsubscribe()
    this.pollingSubscription = undefined
  }

  async load(showLoading = true) {
    if (this.loading) {
      return
    }

    this.loading = true
    if (showLoading) {
      this.patchState({ loading: true })
    }

    this.http
      .get<NotificationsResponse>('/api/notifications')
      .pipe(
        catchError(() =>
          of({
            unreadCount: 0,
            items: [],
          } satisfies NotificationsResponse),
        ),
      )
      .subscribe(response => {
        this.loading = false
        this.patchState({
          unreadCount: response.unreadCount ?? 0,
          items: response.items ?? [],
          loading: false,
        })
      })
  }

  openPanel() {
    this.openPanelFromBell()
  }

  openPanelFromBell() {
    this.patchState({
      panelOpen: true,
      panelAnchor: 'bell',
      panelTop: null,
      panelBottom: null,
      panelLeft: null,
      panelRight: null,
      panelMaxHeight: null,
    })
    if (!this.snapshot.items.length) {
      void this.load()
    }
  }

  openPanelFromMenu(triggerRect: DOMRect) {
    const PADDING = 12
    const vw = window.innerWidth
    const vh = window.innerHeight

    const panelWidth = Math.min(384, vw - PADDING * 2)
    const maxPanelHeight = Math.min(512, Math.floor(vh * 0.7))

    let panelLeft: number | null = null
    let panelRight: number | null = null
    let panelTop: number | null = null
    let panelBottom: number | null = null
    let panelMaxHeight: number | null = null

    if (vw <= 440) {
      panelLeft = PADDING
      panelRight = PADDING
    } else {
      const spaceToRightOfTrigger = vw - triggerRect.right - PADDING
      const spaceToLeftOfTrigger = triggerRect.left - PADDING

      if (triggerRect.left < vw / 2) {
        if (spaceToRightOfTrigger >= panelWidth) {
          panelLeft = triggerRect.right + 8
        } else if (vw - triggerRect.left - PADDING >= panelWidth) {
          panelLeft = Math.max(PADDING, triggerRect.left)
        } else {
          panelLeft = Math.max(PADDING, vw - panelWidth - PADDING)
        }
      } else {
        const distFromRight = vw - triggerRect.right
        if (distFromRight + panelWidth + PADDING <= vw && triggerRect.right >= panelWidth) {
          panelRight = Math.max(PADDING, distFromRight)
        } else if (spaceToLeftOfTrigger >= panelWidth) {
          panelRight = vw - triggerRect.left + 8
        } else {
          panelRight = Math.max(PADDING, vw - panelWidth - PADDING)
        }
      }
    }

    const spaceBelow = vh - triggerRect.bottom - PADDING
    const spaceAbove = triggerRect.top - PADDING
    const isSideFlyout = panelLeft === triggerRect.right + 8 || (panelRight !== null && panelRight === vw - triggerRect.left + 8)

    if (isSideFlyout) {
      const spaceBelowFromItemTop = vh - triggerRect.top - PADDING
      if (spaceBelowFromItemTop >= 240) {
        panelTop = Math.max(PADDING, triggerRect.top)
        panelMaxHeight = Math.min(maxPanelHeight, spaceBelowFromItemTop)
      } else if (spaceAbove > spaceBelow) {
        panelBottom = Math.max(PADDING, vh - triggerRect.bottom)
        panelMaxHeight = Math.min(maxPanelHeight, triggerRect.bottom - PADDING)
      } else {
        panelTop = Math.max(PADDING, triggerRect.top)
        panelMaxHeight = Math.max(160, spaceBelowFromItemTop)
      }
    } else {
      if (spaceBelow >= 280 || (spaceBelow >= spaceAbove && spaceBelow >= 180)) {
        panelTop = triggerRect.bottom + 8
        panelMaxHeight = Math.min(maxPanelHeight, Math.max(160, spaceBelow - 8))
      } else {
        panelBottom = vh - triggerRect.top + 8
        panelMaxHeight = Math.min(maxPanelHeight, Math.max(160, spaceAbove - 8))
      }
    }

    this.patchState({
      panelOpen: true,
      panelAnchor: 'menu',
      panelTop,
      panelBottom,
      panelLeft,
      panelRight,
      panelMaxHeight,
    })

    if (!this.snapshot.items.length) {
      void this.load()
    }
  }

  toggleMenuPanel(triggerRect: DOMRect) {
    if (this.snapshot.panelOpen && this.snapshot.panelAnchor === 'menu') {
      this.closePanel()
      return
    }

    this.openPanelFromMenu(triggerRect)
  }

  closePanel() {
    this.patchState({ panelOpen: false })
  }

  togglePanel() {
    this.toggleBellPanel()
  }

  toggleBellPanel() {
    if (this.snapshot.panelOpen && this.snapshot.panelAnchor === 'bell') {
      this.closePanel()
      return
    }

    this.openPanelFromBell()
  }

  setBellDockedToUserMenu(isDocked: boolean) {
    localStorage.setItem(BELL_DOCKED_TO_USER_MENU_STORAGE_KEY, `${isDocked}`)
    this.patchState({ bellDockedToUserMenu: isDocked })
  }

  get isBellDockedToUserMenu() {
    return this.snapshot.bellDockedToUserMenu
  }

  get stateSnapshot() {
    return this.snapshot
  }

  markAsRead(id: string) {
    this.patchState({ loading: true })
    this.http
      .post<NotificationsResponse>(`/api/notifications/${encodeURIComponent(id)}/read`, {})
      .pipe(catchError(() => of(null)))
      .subscribe(response => {
        if (!response) {
          this.patchState({ loading: false })
          return
        }

        this.patchState({
          unreadCount: response.unreadCount ?? 0,
          items: response.items ?? [],
          loading: false,
        })
      })
  }

  markAllAsRead() {
    this.patchState({ loading: true })
    this.http
      .post<NotificationsResponse>('/api/notifications/read-all', {})
      .pipe(catchError(() => of(null)))
      .subscribe(response => {
        if (!response) {
          this.patchState({ loading: false })
          return
        }

        this.patchState({
          unreadCount: response.unreadCount ?? 0,
          items: response.items ?? [],
          loading: false,
        })
      })
  }

  private patchState(patch: Partial<NotificationsState>) {
    this.stateSubject.next({
      ...this.snapshot,
      ...patch,
    })
  }

  private get snapshot() {
    return this.stateSubject.value
  }
}
